import { inject, Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { fetchAuthSession } from 'aws-amplify/auth';
import { from, NEVER, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { ActiveSecurityContextStateService } from '../active-security/active-security-context.state-service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { AuthenticationService } from '@auth-n/authentication.service';
import { cintraApiAuthHeaders } from './cintra-api-auth-headers';
import { isCloudApiUrl } from './utils/interceptor-utils';

const SESSION_UNCONFIRMED = 'session-unconfirmed';

type AuthHeadersCheck = Record<string, string> | null | typeof SESSION_UNCONFIRMED;

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly authenticationState = inject(ActiveAuthenticationStateService);
  private readonly securityStateService = inject(ActiveSecurityContextStateService);
  // AuthenticationService needs HttpClient, which needs this interceptor: resolved on use to avoid the cycle.
  private readonly injector = inject(Injector);
  private sessionRefresh: Promise<boolean> | null = null;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.authenticationState.isAuthenticated) return next.handle(request);
    if (!isCloudApiUrl(request)) return next.handle(request);

    return from(this.authHeaders(request)).pipe(
      mergeMap((headers) => {
        if (headers === SESSION_UNCONFIRMED) return next.handle(request);
        return headers
          ? next.handle(request.clone({ withCredentials: false, setHeaders: headers }))
          : this.redirectStaleSessionToSignIn(request, next);
      })
    );
  }

  private async authHeaders(request: HttpRequest<any>): Promise<AuthHeadersCheck> {
    const headers = await cintraApiAuthHeaders(this.securityStateService, request.headers);
    if (headers) return headers;

    // An empty read is not proof the session has gone (a fresh origin is still refreshing its access token), and
    // redirecting a user whose session cookies remain signs them straight back in, looping between sign-in and `/`.
    const refreshCompleted = await this.forceSessionRefresh();
    return refreshCompleted ? cintraApiAuthHeaders(this.securityStateService, request.headers) : SESSION_UNCONFIRMED;
  }

  private forceSessionRefresh(): Promise<boolean> {
    this.sessionRefresh ??= fetchAuthSession({ forceRefresh: true })
      .then(
        () => true,
        () => false
      )
      .finally(() => (this.sessionRefresh = null));
    return this.sessionRefresh;
  }

  private redirectStaleSessionToSignIn(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authenticationService = this.injector.get(AuthenticationService);
    if (!authenticationService.isSignOutInProgress && this.authenticationState.isAuthenticated) {
      authenticationService.redirectToSignInAfterSessionExpiry();
    }
    return authenticationService.canNavigateToSignIn ? NEVER : next.handle(request);
  }
}
